import { Suspense } from 'react';

import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { DrawerProvider } from '@cast/design-system';
import { AccessControlProvider } from '@cast/infrastructure-abilities';
import type { Subjects } from '@cast/shared-ability-data-access';

import { SuspenseLoader } from 'components/loaders';
import { ProtectedRoute } from 'components/router';
import {
  flattenNavItems,
  renderConfigRoutes,
  type OneClickLinkItem,
} from '@oneclick/routing';

import { ACCESS_CONTROL_ROUTE_CONFIG } from './accessControlRouteConfig';

const AccessControlRoutes = () => (
  <AccessControlProvider>
    <DrawerProvider>
      <Outlet />
    </DrawerProvider>
  </AccessControlProvider>
);

const AccessControlPages = flattenNavItems(ACCESS_CONTROL_ROUTE_CONFIG);

const nestedSubject = (page: OneClickLinkItem): Subjects | undefined =>
  page.rules && page.rules.length > 1
    ? page.rules[page.rules.length - 1][1]
    : undefined;

const renderWithNestedGuards = (pages: OneClickLinkItem[]) => {
  const plain = pages.filter((p) => !nestedSubject(p));
  const nested = pages.filter((p) => nestedSubject(p));

  return (
    <>
      {renderConfigRoutes({ pages: plain, suspense: false })}
      {nested.map((page) => {
        const subject = nestedSubject(page)!;
        return (
          <Route
            key={`${page.route}-guard-${subject}`}
            element={<ProtectedRoute subject={subject} />}
          >
            {renderConfigRoutes({ pages: [page], suspense: false })}
          </Route>
        );
      })}
    </>
  );
};

export const AccessControlController = () => {
  return (
    <Suspense fallback={<SuspenseLoader />}>
      <Routes>
        <Route element={<AccessControlRoutes />}>
          <Route
            element={<ProtectedRoute subject="OrganizationAccessManagement" />}
          >
            {renderWithNestedGuards(AccessControlPages)}
          </Route>
        </Route>
        <Route index element={<Navigate to="users" replace />} />
        <Route path="*" element={<Navigate to="users" replace />} />
      </Routes>
    </Suspense>
  );
};
